/*
 * Sticky add to cart bar (mobile).
 * The main product form stays the single source of truth: the bar submits it
 * via the `form` attribute and mirrors its state after every variant change.
 */
const VISIBLE_CLASS = "sticky-atc-visible";

class StickyAddToCart extends HTMLElement {
    #intersectionObserver;
    #resizeObserver;
    #stateObserver;
    #submittedFromBar = false;
    #boundOnVariantChange;
    #boundOnSubmitClick;
    #boundOnOptionsClick;
    constructor() {
        super();
        this.#boundOnVariantChange = this.#onVariantChange.bind(this);
        this.#boundOnSubmitClick = () => {
            this.#submittedFromBar = true;
        };
        this.#boundOnOptionsClick = this.#scrollToOptions.bind(this);
    }
    connectedCallback() {
        this.formId = this.dataset.formId;
        this.productElement = this.closest("product-single");
        this.button = this.querySelector(".js-sticky-atc-button");
        this.buttonText = this.querySelector(".js-sticky-atc-button-text");
        this.stagedAction = this.button.querySelector("staged-action");
        this.price = this.querySelector(".js-sticky-atc-price");
        this.variantTitle = this.querySelector(".js-sticky-atc-variant");
        this.image = this.querySelector(".sticky-atc__image");
        this.optionsButton = this.querySelector(".js-sticky-atc-options");

        this.#intersectionObserver = new IntersectionObserver(([entry]) => {
            // Only show the bar once the main button has been scrolled past,
            // not while it is still below the fold.
            this.#setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
        });
        this.#resizeObserver = new ResizeObserver(([entry]) => {
            document.documentElement.style.setProperty("--sticky-atc-height", `${entry.borderBoxSize?.[0]?.blockSize ?? this.offsetHeight}px`);
        });
        this.#resizeObserver.observe(this);
        this.#stateObserver = new MutationObserver(this.#onFormMutation.bind(this));

        // Variant changes are dispatched without bubbling, so listen in the capture phase.
        this.productElement?.addEventListener("on:variant:change", this.#boundOnVariantChange, true);
        this.button.addEventListener("click", this.#boundOnSubmitClick);
        this.optionsButton?.addEventListener("click", this.#boundOnOptionsClick);
        document.documentElement.classList.add("has-sticky-atc");

        this.#observeMainForm();
    }
    disconnectedCallback() {
        this.#intersectionObserver.disconnect();
        this.#resizeObserver.disconnect();
        this.#stateObserver.disconnect();
        this.productElement?.removeEventListener("on:variant:change", this.#boundOnVariantChange, true);
        this.button.removeEventListener("click", this.#boundOnSubmitClick);
        this.optionsButton?.removeEventListener("click", this.#boundOnOptionsClick);
        document.documentElement.classList.remove("has-sticky-atc", VISIBLE_CLASS);
    }
    get mainForm() {
        return document.getElementById(this.formId);
    }
    get mainButton() {
        return this.mainForm?.querySelector(".js-product-add");
    }
    #observeMainForm() {
        this.#intersectionObserver.disconnect();
        this.#stateObserver.disconnect();
        const form = this.mainForm;
        const mainButton = this.mainButton;
        if (!form || !mainButton) {
            this.#setVisible(false);
            return;
        }
        this.#intersectionObserver.observe(mainButton);
        this.#stateObserver.observe(form, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: [ "class", "disabled" ]
        });
        this.#syncButton();
    }
    #setVisible(visible) {
        this.classList.toggle("is-visible", visible);
        this.inert = !visible;
        document.documentElement.classList.toggle(VISIBLE_CLASS, visible);
    }
    #syncButton() {
        const mainButton = this.mainButton;
        if (!mainButton) return;
        this.button.disabled = mainButton.disabled;
        // Use the short label while purchasable, otherwise mirror the main state (e.g. sold out).
        const mainText = mainButton.querySelector("staged-action-text")?.textContent.trim();
        const text = mainButton.disabled ? mainText : this.dataset.addText;
        if (text) this.buttonText.textContent = text;
        const mainStagedAction = mainButton.querySelector("staged-action");
        if (mainStagedAction) {
            this.stagedAction.classList.toggle("is-doing", mainStagedAction.classList.contains("is-doing"));
            this.stagedAction.classList.toggle("is-done", mainStagedAction.classList.contains("is-done"));
        }
    }
    #onFormMutation(mutations) {
        this.#syncButton();
        // Errors are rendered next to the main button, which is off screen while the bar is visible.
        const errorAdded = mutations.some(mutation => Array.from(mutation.addedNodes).some(node => node.classList?.contains("errors")));
        if (errorAdded && this.#submittedFromBar) {
            this.mainForm.querySelector(".errors")?.scrollIntoView({
                behavior: this.#scrollBehavior,
                block: "center"
            });
        }
        const isBusy = this.stagedAction.classList.contains("is-doing") || this.stagedAction.classList.contains("is-done");
        if (!isBusy) this.#submittedFromBar = false;
    }
    #onVariantChange(event) {
        const variant = event.detail?.variant;
        // The product form gets replaced on variant change, so re-attach observers.
        this.#observeMainForm();
        const mainPrice = document.getElementById(`price-${this.dataset.sectionId}`);
        if (mainPrice) this.price.innerHTML = mainPrice.innerHTML;
        if (!variant) return;
        if (this.variantTitle) this.variantTitle.textContent = variant.title;
        const imageSrc = variant.featured_image?.src;
        if (this.image && imageSrc) {
            const src = this.#resizedImageUrl(imageSrc, 96);
            this.image.src = src;
            this.image.srcset = `${src} 1x, ${this.#resizedImageUrl(imageSrc, 192)} 2x`;
        }
    }
    #resizedImageUrl(src, width) {
        const url = new URL(src, window.location.href);
        url.searchParams.set("width", width);
        return url.toString();
    }
    #scrollToOptions() {
        const target = this.productElement?.querySelector(".product-single__box__block--variant_picker") || this.mainButton;
        target?.scrollIntoView({
            behavior: this.#scrollBehavior,
            block: "center"
        });
    }
    get #scrollBehavior() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    }
}

customElements.define("sticky-add-to-cart", StickyAddToCart);
